import { useState } from 'react'
import { useTicketsStore } from '@/stores/tickets'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog'
import { MarkdownRenderer } from '@/components/ui/markdown-renderer'
import {
  Clock,
  User,
  Flag,
  Tag,
  CheckCircle2,
  AlertCircle,
  Edit3,
  Calendar,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import type { Database } from '@/types/database'

type Ticket = Database['public']['Tables']['tickets']['Row']

interface TicketCardProps {
  ticket: Ticket
}

export function TicketCard({ ticket }: TicketCardProps) {
  const { solveTicket, confirmTicket, updateTicket, filterStatus } = useTicketsStore()
  const [isEditMode, setIsEditMode] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [solution, setSolution] = useState('')
  const [confirmedBy, setConfirmedBy] = useState('')

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'bg-green-500'
      case 'medium': return 'bg-yellow-500'
      case 'high': return 'bg-orange-500'
      case 'urgent': return 'bg-red-500'
      default: return 'bg-gray-500'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-500'
      case 'in_progress': return 'bg-yellow-600'
      case 'solved': return 'bg-green-600'
      case 'confirmed': return 'bg-emerald-600'
      case 'closed': return 'bg-gray-600'
      default: return 'bg-gray-500'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open': return 'Offen'
      case 'in_progress': return 'In Bearbeitung'
      case 'solved': return 'Gelöst'
      case 'confirmed': return 'Bestätigt'
      case 'closed': return 'Geschlossen'
      default: return status
    }
  }

  const handleSolve = async () => {
    if (!solution.trim() || !confirmedBy) {
      alert('Bitte geben Sie eine Lösung und einen Bearbeiter an.')
      return
    }

    await solveTicket(ticket.id, solution.trim(), confirmedBy)
    setSolution('')
    setConfirmedBy('')
    setIsEditMode(false)
  }

  const handleConfirm = async () => {
    if (!confirmedBy) {
      alert('Bitte wählen Sie einen Bestätiger aus.')
      return
    }

    await confirmTicket(ticket.id, confirmedBy)
    setConfirmedBy('')
  }

  const handleStatusChange = async (newStatus: string) => {
    // If changing to 'solved', show modal for solution input
    if (newStatus === 'solved') {
      setIsEditMode(true)
      return
    }
    await updateTicket(ticket.id, { status: newStatus as any })
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE')
  }

  // Determine card styling based on status and filters
  const isSolved = ticket.status === 'solved' || ticket.status === 'confirmed'
  const isOpen = ticket.status === 'open'
  const showingAllTickets = !filterStatus // No status filter applied

  const getCardClassName = () => {
    let baseClass = 'w-full transition-all duration-200'

    // Apply transparency for solved tickets when showing all tickets
    if (isSolved && showingAllTickets) {
      baseClass += ' opacity-80'
    }

    return baseClass
  }

  const getCardStyle = () => {
    // Add blue border for open tickets when showing all tickets
    if (isOpen && showingAllTickets) {
      return {
        borderColor: '#3b82f6', // blue-500
        borderWidth: '2px'
      }
    }
    return {}
  }

  return (
    <Card className={getCardClassName()} style={getCardStyle()}>
      <CardHeader className="pb-3">
        <div
          className="flex items-start justify-between gap-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 -m-3 p-3 rounded-md transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg leading-none tracking-tight truncate">
              {ticket.name}
            </h3>
            <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              <span>{ticket.created_by}</span>
              <Clock className="h-4 w-4 ml-2" />
              <span>{formatDate(ticket.created_at)}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge
              variant="secondary"
              className={`text-white ${getPriorityColor(ticket.priority)}`}
            >
              <Flag className="h-3 w-3 mr-1" />
              {ticket.priority}
            </Badge>
            <Badge
              variant="secondary"
              className={`text-white ${getStatusColor(ticket.status)}`}
            >
              {getStatusLabel(ticket.status)}
            </Badge>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Description */}
        <div className={`text-sm ${isExpanded ? '' : 'line-clamp-3'}`}>
          <MarkdownRenderer
            content={ticket.description || ''}
            className="prose prose-sm prose-neutral dark:prose-invert max-w-none prose-headings:text-base prose-h1:text-base prose-h2:text-sm prose-h3:text-sm prose-h4:text-sm prose-h5:text-sm prose-h6:text-sm"
          />
        </div>

        {/* Category */}
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-muted-foreground" />
          <Badge variant="outline">{ticket.category}</Badge>
        </div>

        {/* Solution Display */}
        {ticket.solution && (
          <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-md border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="font-medium text-green-800 dark:text-green-200">Lösung</span>
            </div>
            <div className="text-sm text-green-700 dark:text-green-300">
              <MarkdownRenderer
                content={ticket.solution || ''}
                className="prose prose-sm prose-green dark:prose-invert max-w-none prose-headings:text-base prose-h1:text-base prose-h2:text-sm prose-h3:text-sm prose-h4:text-sm prose-h5:text-sm prose-h6:text-sm"
              />
            </div>
            {ticket.solved_at && (
              <div className="flex items-center gap-2 mt-2 text-xs text-green-600 dark:text-green-400">
                <Calendar className="h-3 w-3" />
                <span>Gelöst: {formatDate(ticket.solved_at)}</span>
              </div>
            )}
          </div>
        )}

        {/* Timestamps */}
        {(ticket.confirmed_at || ticket.solved_at) && (
          <div className="text-xs text-muted-foreground space-y-1">
            {ticket.solved_at && (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3" />
                <span>Gelöst: {formatDate(ticket.solved_at)}</span>
              </div>
            )}
            {ticket.confirmed_at && (
              <div className="flex items-center gap-2">
                <AlertCircle className="h-3 w-3" />
                <span>Bestätigt: {formatDate(ticket.confirmed_at)} von {ticket.confirmed_by}</span>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons - Only show when expanded */}
        {isExpanded && (
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            {/* Status Change */}
            <Select
              value={ticket.status}
              onValueChange={handleStatusChange}
            >
              <SelectTrigger className="w-auto">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Offen</SelectItem>
                <SelectItem value="in_progress">In Bearbeitung</SelectItem>
                <SelectItem value="solved">Gelöst</SelectItem>
                <SelectItem value="confirmed">Bestätigt</SelectItem>
                <SelectItem value="closed">Geschlossen</SelectItem>
              </SelectContent>
            </Select>

          {/* Solve Ticket Dialog */}
          {ticket.status !== 'solved' && ticket.status !== 'confirmed' && (
            <Dialog open={isEditMode} onOpenChange={setIsEditMode}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Lösen
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ticket lösen</DialogTitle>
                  <DialogDescription>
                    Geben Sie eine Lösung an und wählen Sie den Bearbeiter, um das Ticket als gelöst zu markieren.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Lösung</label>
                    <Textarea
                      placeholder="Beschreiben Sie die Lösung..."
                      value={solution}
                      onChange={(e) => setSolution(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Bearbeiter</label>
                    <Select value={confirmedBy} onValueChange={setConfirmedBy}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Bearbeiter auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Bernhard">Bernhard</SelectItem>
                        <SelectItem value="Thomas">Thomas</SelectItem>
                        <SelectItem value="Christoph">Christoph</SelectItem>
                        <SelectItem value="Stefan">Stefan</SelectItem>
                        <SelectItem value="Mathias">Mathias</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSolve} className="flex-1">
                      Ticket als gelöst markieren
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setIsEditMode(false)}
                    >
                      Abbrechen
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Confirm Solution */}
          {ticket.status === 'solved' && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  Bestätigen
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Lösung bestätigen</DialogTitle>
                  <DialogDescription>
                    Wählen Sie den Bestätiger, um die Lösung als verifiziert zu markieren.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Bestätigt von</label>
                    <Select value={confirmedBy} onValueChange={setConfirmedBy}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Bestätiger auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Bernhard">Bernhard</SelectItem>
                        <SelectItem value="Thomas">Thomas</SelectItem>
                        <SelectItem value="Christoph">Christoph</SelectItem>
                        <SelectItem value="Stefan">Stefan</SelectItem>
                        <SelectItem value="Mathias">Mathias</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleConfirm} className="w-full">
                    Lösung bestätigen
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}